import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/AdminService';

interface OrgOption {
  id: number;
  name: string;
}

/**
 * Admin-only picker for the organization this account works in.
 *
 * Meant for the Cata Support account: switching into an org shows that org's
 * orders, catalogs and settings as its members see them, and orders placed
 * while switched in are shown to members as created by this account's name.
 * Hidden while impersonating (impersonation acts as the other user instead).
 */
const WorkingOrgSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, isImpersonating } = useAuth();
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    if (!isAdmin || isImpersonating) return;
    let cancelled = false;
    adminService
      .getOrganizations()
      .then(res => {
        if (cancelled) return;
        const list = (res.data.organizations || [])
          .map((o: any) => ({ id: o.id, name: o.name }))
          .sort((a: OrgOption, b: OrgOption) => a.name.localeCompare(b.name));
        setOrgs(list);
      })
      .catch(() => !cancelled && setError(t('admin.workingOrg.loadFailed', { defaultValue: 'Could not load organizations' })));
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isImpersonating, t]);

  if (!isAdmin || isImpersonating) return null;

  const current = currentUser?.organization_id ?? null;

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === '' ? null : Number(e.target.value);
    if (value === current) return;
    setSaving(true);
    setError(null);
    try {
      await adminService.setWorkingOrganization(value);
      // Orders, catalogs, settings and cart are all org-scoped and cached in
      // various contexts — a full reload is the reliable way to switch.
      window.location.assign('/');
    } catch {
      setError(t('admin.workingOrg.switchFailed', { defaultValue: 'Could not switch organization' }));
      setSaving(false);
    }
  };

  return (
    <div className="px-3 py-2">
      <label className="flex items-center gap-2 text-xs text-gray-500 font-jakarta mb-1">
        <Building2 className="w-3.5 h-3.5" />
        {t('admin.workingOrg.label', { defaultValue: 'Working in organization' })}
      </label>
      <select
        value={current ?? ''}
        onChange={onChange}
        disabled={saving}
        className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-800 font-jakarta disabled:opacity-60"
      >
        <option value="">{t('admin.workingOrg.none', { defaultValue: '— No organization —' })}</option>
        {current != null && !orgs.some(o => o.id === current) && (
          <option value={current}>#{current}</option>
        )}
        {orgs.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default WorkingOrgSwitcher;
